import moment from "moment";
import React, {
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRecoilState } from "recoil";
import phoneIcon from "../../assets/imgs/phone.png";
import sendIcon from "../../assets/imgs/send.png";
import MessagesHistoryCard from "../../components/MessagesHistoryCard";
import ChatMessage from "../../components/ChatMessage";
import ControlledInput from "../../components/ControlledInput";
import DateBox from "../../components/DateBox";
import Layout from "../../components/Layout";
import NotFoundMessage from "../../components/NotFoundMessage";
import PngIcon from "../../components/PngIcon";
import { Message } from "../../interfaces/Message";
import { Permission, User } from "../../interfaces/User";
import { api } from "../../services/api";
import { currentChatMessagesState } from "../../states/currentChatMessagesState";
import selectedUserToChatState, {
  SelectedUserToChat,
} from "../../states/selectedUserToChatState";
import userState from "../../states/userState";
import useStartCall from "../../utils/startCall";
import { notify, ToastType } from "../../utils/toast";
import {
  ChatBody,
  ChatContainer,
  ChatFoot,
  ChatHead,
  Container,
  InputMessageContainer,
  ScrollLoadingContainer,
  UserCards,
  UserCardsContainer,
} from "./styles";
import setDateBoxes from "../../utils/setDateBoxes";
import LoadingMessage from "../../components/LoadingMessage";
import notificationsState from "../../states/notificationsState";
import { Residential } from "../../interfaces/Residential";
import { Condominium } from "../../interfaces/Condominium";
import { Tower } from "../../interfaces/Tower";
import { Apartment } from "../../interfaces/Apartment";
import { BeatLoader, ClipLoader } from "react-spinners";
import getCompany from "../../utils/getCompany";
import _ from "lodash";
import { MdAttachFile } from "react-icons/md";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { MdCameraAlt } from "react-icons/md";
import WebcamModal from "../../components/WebcamModal";

type Structure = { name: string; id: number };

type MessageUser = {
  apartment: Structure | null;
  condominium: Structure | null;
  id: number;
  imageUUID: string;
  name: string;
  permission: string;
  phone: string;
  residential: Structure | null;
  shouldCallVoip: boolean;
  surname: string;
  tower: Structure | null;
};

type PorterMessage = {
  content: string;
  createdAt: string;
  id: number;
  idUserFrom: number;
  idUserTo: number;
  status: string;
  time: string;
  updatedAt: string;
  userFrom: MessageUser;
  userTo: MessageUser;
};

type MessageNotification = {
  apartment: string;
  condominium: string;
  imageUUID: string;
  messageId: string;
  name: string;
  permission: string;
  residential: string;
  shouldCallVoip: string;
  text: string;
  tower: string;
  userId_to: string;
  user_id: string;
  user_name: string;
  user_phone: string;
  imageUUID_to?: string;
  name_to?: string;
  apartment_to?: string;
  tower_to?: string;
  condominium_to?: string;
  residential_to?: string;
  permission_to?: string;
  user_phone_to?: string;
};

function Messages() {
  const [user] = useRecoilState(userState);

  const startCall = useStartCall();

  const [selectedUserToChat, setSelectedUserToChat] = useRecoilState(
    selectedUserToChatState
  );
  const [currentChatMessages, setCurrentChatMessages] = useRecoilState(
    currentChatMessagesState
  );
  const [messagesHistory, setMessagesHistory] = useState<
    Array<User | SelectedUserToChat>
  >([]);
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const [isChatFetching, setIsChatFetching] = useState<boolean>(false);

  const [isHistoryFetched, setIsHistoryFetched] = useState<boolean>(false);
  const [isHistoryFetching, setIsHistoryFetching] = useState<boolean>(false);
  const [isScrollLoading, setIsScrollLoading] = useState(false);

  const [search, setSearch] = useState<string>("");

  const [isAllChatsLoaded, setIsAllChatsLoaded] = useState(false);

  const [messagesHistoryCards, setMessagesHistoryCards] = useState<
    Array<JSX.Element | boolean>
  >([]);

  const [sendingChatImageUrl, setSendingChatImageUrl] = useState<any>(null);
  const [sendingChatImage, setSendingChatImage] = useState<any>(null);

  const [openCamera, setOpenCamera] = useState(false);

  const handleSelectChat = useCallback(
    async (selectedUser: User | SelectedUserToChat) => {
      setSelectedUserToChat(selectedUser);

      if (selectedUser.permission?.includes("Porter")) {
        const response = await api.get(
          `/messages/${user.id}/${selectedUser.id}/0`
        );

        const messages: Message[] = response.data.messages;

        if (messages.length) setCurrentChatMessages(setDateBoxes(messages));
        else setCurrentChatMessages([]);
      } else {
        const response = await api.get(
          `/messages/porter/${selectedUser.id}/${user.residentialId}`
        );

        const messages = response.data.map((item: any) => ({
          ...item,
          dateBox: moment(item.createdAt).format("DD/MM/YYYY"),
        }));

        if (messages.length) setCurrentChatMessages(setDateBoxes(messages));
        else setCurrentChatMessages([]);
      }

      setIsChatFetching(false);
    },
    [setCurrentChatMessages, setSelectedUserToChat, user.id, user.residentialId]
  );

  const fetchMessagesHistory = useCallback(
    async (offset: number, residentialId: number, userId: number) => {
      if (user.id) {
        const response = await api.get(
          `/messages/lastChatPorter/20/${offset}/${residentialId}/${userId}`
        );
        const porterMessages: PorterMessage[] = response.data;

        if (porterMessages.length === 0) {
          setIsAllChatsLoaded(true);
          return;
        }

        const isCommercialResidential =
          user?.residential?.labelType === "commercial";

        const allLastMessages: (User | SelectedUserToChat)[] = [];

        if (isCommercialResidential) {
          for (let i = 0; i < porterMessages.length; i++) {
            let chatUser: MessageUser;

            if (porterMessages[i].idUserFrom === user.id)
              chatUser = porterMessages[i].userTo;
            else if (porterMessages[i].idUserTo === user.id)
              chatUser = porterMessages[i].userFrom;
            else if (porterMessages[i].userFrom.permission.includes("Porter"))
              chatUser = porterMessages[i].userTo;
            else chatUser = porterMessages[i].userFrom;

            const company = await getCompany(
              chatUser.residential?.id as number,
              chatUser.condominium?.id as number,
              chatUser.tower?.id as number,
              chatUser.apartment?.id as number
            );

            const item = {
              id: chatUser.id,
              name: chatUser.name,
              surname: chatUser.surname,
              phone: chatUser.phone,
              content: porterMessages[i].content,
              company,
              //lastMessageUnseen: boolean,
              residential: chatUser.residential as Residential,
              condominium: chatUser.condominium as Condominium,
              tower: chatUser.tower as Tower,
              apartment: chatUser.apartment as Apartment,
              apartmentId: chatUser.apartment?.id,
              towerId: chatUser.tower?.id,
              condominiumId: chatUser.condominium?.id,
              residentialId: chatUser.residential?.id as number,
              imageUUID: chatUser.imageUUID,
              permission: chatUser.permission as Permission,
              shouldCallVoip: chatUser.shouldCallVoip,
              createdAt: porterMessages[i].createdAt,
              updatedAt: porterMessages[i].updatedAt,
            };

            allLastMessages.push(item);
          }
        } else
          allLastMessages.push(
            ...porterMessages.map((message): User => {
              let chatUser: MessageUser;

              if (message.idUserFrom === user.id) chatUser = message.userTo;
              else if (message.idUserTo === user.id)
                chatUser = message.userFrom;
              else if (message.userFrom.permission.includes("Porter"))
                chatUser = message.userTo;
              else chatUser = message.userFrom;

              return {
                id: chatUser.id,
                name: chatUser.name,
                surname: chatUser.surname,
                phone: chatUser.phone,
                content: message.content,
                //lastMessageUnseen: boolean,
                residential: chatUser.residential as Residential,
                condominium: chatUser.condominium as Condominium,
                tower: chatUser.tower as Tower,
                apartment: chatUser.apartment as Apartment,
                apartmentId: chatUser.apartment?.id,
                towerId: chatUser.tower?.id,
                condominiumId: chatUser.condominium?.id,
                residentialId: chatUser.residential?.id as number,
                imageUUID: chatUser.imageUUID,
                permission: chatUser.permission as Permission,
                shouldCallVoip: chatUser.shouldCallVoip,
                createdAt: message.createdAt,
                updatedAt: message.updatedAt,
              };
            })
          );

        const userExists = allLastMessages.some(
          (fetched) => fetched.id === selectedUserToChat.id
        );

        if (Object.values(selectedUserToChat).length > 0 && !userExists)
          setMessagesHistory([selectedUserToChat, ...allLastMessages]);
        else
          setMessagesHistory((messages) => [...messages, ...allLastMessages]);
      }
    },
    [user.id, user?.residential?.labelType, selectedUserToChat]
  );

  const [sendingMessage, setSendingMessage] = useState<string>("");
  const [sendMessageEnabled, setSendMessageEnabled] = useState<boolean>(false);

  const chatBodyRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSendMessage = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      const sendingMessageAux = sendingMessage;
      const currentChatMessagesAux = [...currentChatMessages];

      try {
        setSendingMessage("");
        setSendingChatImage(null);
        setSendingChatImageUrl(null);

        const createdAt = moment().format();
        const currentTime = moment().format("HH:mm");

        const message = {
          text: sendingMessageAux,
          time: currentTime,
          to: selectedUserToChat.id,
          user_name: user.name + (user.surname ? " " + user.surname : ""),
          user_id: user.id,
          user_phone: [user.phone],
          fileData: sendingChatImage,
        };
        const formData = new FormData();
        Object.entries(message).forEach(([key, value]) => {
          formData.append(key, value);
        });

        //if (sendingMessageAux) {
        const res = await api.post("/messages/send", formData);
        const {
          data: { imageUUID },
        } = res;

        const sentMessage = {
          idUserFrom: user.id,
          idUserTo: selectedUserToChat.id,
          content: sendingMessageAux,
          createdAt,
          time: currentTime,
          imageUUID,
          isImg: !_.isNil(imageUUID),
        };
        setCurrentChatMessages([...currentChatMessages, sentMessage]);
        //}
      } catch (e) {
        notify(
          "Ops... ocorreu um erro ao tentar enviar sua mensagem!",
          ToastType.error
        );

        setSendingMessage(sendingMessageAux);
        setCurrentChatMessages(currentChatMessagesAux);
      }
    },
    [
      sendingMessage,
      currentChatMessages,
      selectedUserToChat.id,
      user.name,
      user.surname,
      user.id,
      user.phone,
      sendingChatImage,
      setCurrentChatMessages,
    ]
  );

  const messageNotificationCallback: (
    this: ServiceWorkerContainer,
    ev: MessageEvent<any>
  ) => any = useCallback(
    (event) => {
      if (event.data.data.text || event.data.data.text_imageUUID) {
        const notificationData: MessageNotification = event.data.data;
        const jsonUser = localStorage.getItem("guugWebPorter.user");
        let localStorageUser: User | undefined;
        if (jsonUser) localStorageUser = JSON.parse(jsonUser);
        //Notification sended from same user
        if (notificationData.user_id === String(localStorageUser?.id)) return;

        let newChat: any;
        if (
          notificationData.permission.includes("Porter") &&
          notificationData.userId_to !== String(localStorageUser?.id)
        )
          newChat = {
            id: +notificationData.userId_to,
            name: notificationData.name_to,
            phone: notificationData.user_phone_to,
            residential: {
              name: notificationData.residential_to,
            } as Residential,
            condominium: {
              name: notificationData.condominium_to,
            } as Condominium,
            tower: { name: notificationData.tower_to } as Tower,
            apartment: { name: notificationData.apartment_to } as Apartment,
            residentialId: localStorageUser?.residentialId,
            shouldCallVoip: false,
            content: notificationData.text,
            imageUUID: notificationData.imageUUID_to,
            permission: notificationData.permission_to as Permission,
          };
        else
          newChat = {
            id: +notificationData.user_id,
            name: notificationData.name,
            phone: JSON.parse(notificationData.user_phone)[0],
            residential: { name: notificationData.residential } as Residential,
            condominium: { name: notificationData.condominium } as Condominium,
            tower: { name: notificationData.tower } as Tower,
            apartment: { name: notificationData.apartment } as Apartment,
            residentialId: localStorageUser?.residentialId,
            shouldCallVoip: Boolean(notificationData.shouldCallVoip),
            content: notificationData.text,
            imageUUID: notificationData.imageUUID,
            permission: notificationData.permission as Permission,
          };

        setMessagesHistory((messages) => {
          const id = messages.findIndex(
            (message) => message.id === +newChat.id
          );

          const messagesAux = [...messages];

          if (id !== -1) messagesAux.splice(id, 1);

          messagesAux.unshift(newChat);
          return messagesAux;
        });
      }
    },
    [setMessagesHistory]
  );

  const handleSetChatImage = async (dataURL: string) => {
    setSendingChatImageUrl(dataURL);

    setSendMessageEnabled(true);

    const res = await fetch(dataURL as any);
    const blob = await res.blob();

    setSendingChatImage(blob);
  };

  useEffect(() => {
    setIsHistoryFetching(true);

    if (user.id && user.residentialId) {
      fetchMessagesHistory(0, user.residentialId, user.id).then(() => {
        setIsHistoryFetched(true);
        setIsHistoryFetching(false);
      });
    }
  }, [user.id, user.residentialId]);

  useEffect(() => {
    setTimeout(() => {
      const elemHeight = chatBodyRef?.current?.scrollHeight;

      if (elemHeight) chatBodyRef?.current?.scroll(0, elemHeight);
    }, 500);
  }, [currentChatMessages]);

  useEffect(() => {
    const cards = messagesHistory
      .map((user, i) => {
        return (
          ((user.name &&
            user.name.toLowerCase().includes(search.toLowerCase())) ||
            search === "") && (
            <MessagesHistoryCard
              size="lg"
              selected={user.id === selectedUserToChat.id}
              key={i}
              onClick={() => {
                setIsChatFetching(true);

                handleSelectChat(user);
              }}
              user={user}
            />
          )
        );
      })
      .filter((card) => card);

    setMessagesHistoryCards(cards);
  }, [selectedUserToChat.id, handleSelectChat, messagesHistory, search]);

  useEffect(() => {
    handleSelectChat(selectedUserToChat);

    const someMessageNotificationFromSelectedUser = [...notifications].some(
      (item) =>
        item.type === "MessageNotification" &&
        parseInt(item.data.user_id) === selectedUserToChat.id
    );

    if (someMessageNotificationFromSelectedUser) {
      const newNotifications = [...notifications].filter(
        (item) =>
          item.type !== "PhotoValidationNotification" &&
          (item.type === "OpenDoorNotification" ||
            item.type === "CallNotification" ||
            item.type === "AccessNotification" ||
            item.type === "Default" ||
            item.type === "EmergencyNotification" ||
            item.type === "VehicleNotification" ||
            item.type === "PackageNotification" ||
            parseInt(item.data.user_id) !== selectedUserToChat.id)
      );

      setNotifications(newNotifications);
      localStorage.setItem(
        `guugWebPorter.notifications.user${user.id}`,
        JSON.stringify(newNotifications)
      );
    }
  }, [
    handleSelectChat,
    notifications,
    selectedUserToChat,
    setNotifications,
    user.id,
  ]);

  useEffect(() => {
    navigator.serviceWorker.addEventListener(
      "message",
      messageNotificationCallback
    );
  }, []);

  return (
    <Layout>
      {openCamera && (
        <WebcamModal
          setOpen={setOpenCamera}
          setUserImage={async (img: string) => await handleSetChatImage(img)}
          shouldVerifyUserImage={false}
        />
      )}
      <Container>
        {(isHistoryFetching && <LoadingMessage text="Carregando..." />) || (
          <UserCardsContainer>
            <ControlledInput
              label=""
              id=""
              placeholder="Pesquisar por nome..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
            <UserCards
              onScroll={
                !isAllChatsLoaded
                  ? async (e) => {
                      const elem = e.currentTarget;
                      const { scrollHeight, scrollTop, clientHeight } = elem;
                      const heightPosition = scrollTop + clientHeight;

                      if (scrollHeight - heightPosition <= 4) {
                        setIsScrollLoading(true);

                        await fetchMessagesHistory(
                          messagesHistoryCards.length,
                          user.residentialId,
                          user.id
                        );

                        setIsScrollLoading(false);
                      }
                    }
                  : undefined
              }
            >
              {messagesHistoryCards.length > 0 ? (
                messagesHistoryCards
              ) : isHistoryFetched ? (
                <NotFoundMessage message="Nenhum usuário encontrado." />
              ) : (
                ""
              )}
            </UserCards>
            {isScrollLoading && (
              <ScrollLoadingContainer>
                <BeatLoader color="#0400ff" />
              </ScrollLoadingContainer>
            )}
          </UserCardsContainer>
        )}
        <ChatContainer>
          <ChatHead>
            <h4>{`${selectedUserToChat.name ?? ""} ${
              selectedUserToChat.surname ?? ""
            }`}</h4>
            <button
              disabled={!selectedUserToChat.id}
              onClick={async () => {
                try {
                  if (
                    selectedUserToChat.phone !== "" &&
                    selectedUserToChat.permission
                  ) {
                    await startCall(selectedUserToChat as User);
                  }
                } catch {
                  notify("Ops... Erro ao iniciar chamada!", ToastType.error);
                }
              }}
            >
              <PngIcon icon={phoneIcon} />
            </button>
          </ChatHead>
          <ChatBody ref={chatBodyRef} id="chat-body">
            {(isChatFetching && (
              <LoadingMessage
                parentType="not-flex-parent"
                text="Carregando..."
              />
            )) ||
              (currentChatMessages.length > 0 &&
                currentChatMessages.map((message, i) => {
                  const time = moment(message.createdAt).format("HH:mm");

                  return (
                    <React.Fragment
                      key={`${message.idUserFrom}${message.idUserTo}${i}`}
                    >
                      {message.dateBox && <DateBox time={message.dateBox} />}
                      <ChatMessage
                        isReceivedMessage={user.id !== message.idUserFrom}
                        messageContent={message.content}
                        imageUUID={message.imageUUID}
                        time={time}
                        username={
                          user.id !== message.idUserFrom && message.userFrom
                            ? message.userFrom.name +
                              (message.userFrom.surname
                                ? " " + message.userFrom.surname
                                : "")
                            : undefined
                        }
                      />
                    </React.Fragment>
                  );
                }))}
          </ChatBody>
          <ChatFoot>
            <form onSubmit={handleSendMessage}>
              <input
                ref={fileInputRef}
                style={{ display: "none" }}
                id="chat-image"
                type="file"
                accept=".jpg, .png"
                max={1}
                disabled={!selectedUserToChat.id}
                onChange={async (e) => {
                  const input = e.target;

                  const reader = new FileReader();
                  reader.onload = async function () {
                    const dataURL = reader.result;

                    await handleSetChatImage(dataURL as string);
                  };
                  if (input.files) reader.readAsDataURL(input.files[0]);
                }}
              />

              <div style={{ width: "100%", marginBottom: 5 }}>
                {sendingChatImageUrl && (
                  <div
                    style={{
                      margin: "auto",
                      width: "fit-content",
                      position: "relative",
                    }}
                  >
                    <button
                      onClick={() => {
                        if (fileInputRef?.current)
                          fileInputRef.current.value = "";

                        setSendingChatImage(null);
                        setSendingChatImageUrl(null);

                        setSendMessageEnabled(false);
                      }}
                      type="button"
                      style={{
                        position: "absolute",
                        right: -10,
                        top: -10,
                        background: "none",
                        border: "none",
                      }}
                    >
                      <IoMdCloseCircleOutline color="#a40a0a" size={40} />
                    </button>
                    <img
                      alt=""
                      src={sendingChatImageUrl}
                      style={{ maxHeight: 400, maxWidth: 400 }}
                    />
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 5 }}>
                <InputMessageContainer>
                  <input
                    placeholder="Digite sua mensagem aqui..."
                    disabled={!selectedUserToChat.id}
                    value={sendingMessage}
                    onChange={(e) => {
                      const input = e.target.value;

                      setSendMessageEnabled(input.length > 0);

                      setSendingMessage(input);
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "none",
                      border: "none",
                    }}
                  >
                    <button
                      onClick={() => setOpenCamera(true)}
                      type="button"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <MdCameraAlt
                        size={28}
                        color={!selectedUserToChat.id ? "#ddd" : "#aaa"}
                      />
                    </button>
                    <label
                      style={{
                        cursor: !selectedUserToChat.id
                          ? "not-allowed"
                          : "pointer",
                        display: "flex",
                        alignItems: "center",
                      }}
                      htmlFor="chat-image"
                    >
                      <MdAttachFile
                        size={28}
                        color={!selectedUserToChat.id ? "#ddd" : "#aaa"}
                      />
                    </label>
                  </div>
                </InputMessageContainer>
                <button disabled={!sendMessageEnabled} type="submit">
                  <PngIcon icon={sendIcon} />
                </button>
              </div>
            </form>
          </ChatFoot>
        </ChatContainer>
      </Container>
    </Layout>
  );
}

export default Messages;
